import type { SubtaskWithResult } from './types';

// #2732 stage 7b: what an AI task is, for its label, icon and send state.
// Routed work names it in `metadata.ai_work_route` (how, and for a read
// `read_as`); the legacy `ai_action` label is read only for work without a
// route, so the Web keeps working once the server stops writing the label.
// Kinds that mean the same as a legacy label reuse that label's key.

const READ_KINDS: Record<string, string> = {
  calendar_availability: 'check_calendar',
  source: 'summarize_info',
  compare: 'compare_options',
  lookup: 'lookup_info',
};

const VERB_KINDS: Record<string, string> = {
  draft: 'draft_message',
  send: 'send_message',
  schedule: 'create_event',
};

/** Kinds whose approved result the AI still carries out (sends, writes the calendar). */
const DISPATCH_KINDS = new Set(['send_message', 'create_event', 'delete_event', 'respond_invite']);

type AiWorkKindInput = Pick<SubtaskWithResult, 'ai_action' | 'metadata'>;

function routeOf(st: AiWorkKindInput): Record<string, unknown> | null {
  const route = st.metadata?.ai_work_route;
  if (!route || typeof route !== 'object' || Array.isArray(route)) return null;
  const value = route as Record<string, unknown>;
  return typeof value.how === 'string' && value.how ? value : null;
}

/** Whether the task carries a route; its kind then comes from the route, not from `ai_action`. */
export function hasAiWorkRoute(st: AiWorkKindInput): boolean {
  return routeOf(st) !== null;
}

/** The task's kind: a key of AI_ACTION_LABELS_WEB, or null when nothing names it. */
export function aiWorkKind(st: AiWorkKindInput): string | null {
  const route = routeOf(st);
  if (!route) return st.ai_action || null;
  const how = route.how as string;
  if (how === 'read') {
    const readAs = typeof route.read_as === 'string' ? READ_KINDS[route.read_as] : undefined;
    // A read routed before read_as existed (2026-10-19) still says it in ai_action.
    return readAs ?? st.ai_action ?? 'lookup_info';
  }
  if (how === 'delete' && route.where === 'google_calendar') return 'delete_event';
  return VERB_KINDS[how] ?? how;
}

/** Whether an approved result still waits for the AI to carry it out. */
export function aiWorkDispatches(st: AiWorkKindInput & Pick<SubtaskWithResult, 'ai_result'>): boolean {
  return DISPATCH_KINDS.has(st.ai_result?.action_type || aiWorkKind(st) || '');
}
